import {
  addDateOnlyDays, compareDateOnly, dayOfWeek, formatDateOnly, parseDateOnly,
  timestampToDateOnly, todayDateOnly
} from "../core/date-navigation.js";

// Date-bounded requests end at the close of a session, so leave room for that
// day's remaining bars as well as the visible range and indicator warmup.
export function initialHistoryPlan({ historyCount = 3000, restoredViewport = null, timeframe = "15m",
  timeZone = "America/New_York", allowLatest = false, now = Date.now() } = {}) {
  const configured = Math.max(10, Math.min(5000, Math.ceil(historyCount)));
  const restored = Number.isFinite(Number(restoredViewport?.timestamp)) && Number(restoredViewport?.visibleCount) > 0;
  const visible = restored ? Math.min(5000, Math.ceil(restoredViewport.visibleCount)) : 120;
  const intraday = /^(\d+)(m|h)$/.exec(timeframe);
  const interval = intraday ? Number(intraday[1]) * (intraday[2] === 'h' ? 60 : 1) : null;
  const calendar = /^(\d+)(D|W|M)$/.exec(timeframe);
  const daysPerBar = calendar ? Number(calendar[1]) * (calendar[2] === 'W' ? 5 : calendar[2] === 'M' ? 21 : 1) : 1;
  const barsPerDay = interval ? Math.ceil(390 / interval) : 1 / daysPerBar;
  const margin = restored ? Math.max(300, barsPerDay + 60) : 300;
  const targetCount = Math.min(5000, Math.max(configured, restored ? visible + margin : 0));
  const count = Math.min(targetCount, Math.max(500, visible + margin));
  const currentOptions = calendar ? { includeCurrent: false } : {};
  if (!restored) return { count, targetCount, endDate: null, ...currentOptions };
  const centerDate = timestampToDateOnly(Number(restoredViewport.timestamp), timeZone);
  const today = todayDateOnly(timeZone, now);
  if (!centerDate || !today) return { count, targetCount, endDate: null, ...currentOptions };
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(Number(restoredViewport.timestamp)));
  const localMinutes = Number(parts.find(part => part.type === "hour")?.value) * 60
    + Number(parts.find(part => part.type === "minute")?.value);
  const remaining = interval ? Math.max(0, Math.min(barsPerDay, Math.ceil((16 * 60 - localMinutes) / interval))) : 0;
  let sessions = Math.ceil(Math.max(0, visible / 2 - remaining) / barsPerDay);
  // Weekdays overestimate available daily bars around exchange holidays. Leave
  // extra sessions on the right so restoring the center cannot clamp it earlier.
  if (calendar) sessions += Math.min(Math.ceil(sessions * 0.05) + 2, Math.max(0, count - visible));
  let end = centerDate;
  while (sessions > 0) {
    end = addDateOnlyDays(end, 1);
    if (![0, 6].includes(dayOfWeek(end))) sessions -= 1;
  }
  if (compareDateOnly(end, today) >= 0) {
    const dated = formatDateOnly(today);
    return { count, targetCount, endDate: allowLatest ? null : dated,
      fallbackEndDate: allowLatest ? dated : null, ...(allowLatest ? currentOptions : {}) };
  }
  return { count, targetCount, endDate: formatDateOnly(end) };
}

export async function loadInitialHistory(load, plan, signal) {
  signal?.throwIfAborted();
  let includeCurrent = plan.includeCurrent !== false;
  let endDate = plan.endDate;
  let liveDenied = false;
  let result;
  try {
    result = await load({ count: plan.count, endDate, includeCurrent, signal });
    // A newly listed symbol may have no completed sessions yet. There is no
    // first chart to paint in that case, so try its current session once.
    if (!includeCurrent && !plan.endDate && !result.data?.length && !signal?.aborted) {
      includeCurrent = true;
      result = await load({ count: plan.count, endDate: plan.endDate, includeCurrent, signal });
    }
  } catch (error) {
    if (error?.status !== 403 || !plan.fallbackEndDate || signal?.aborted) throw error;
    endDate = plan.fallbackEndDate;
    includeCurrent = true;
    liveDenied = true;
    result = await load({ count: plan.count, endDate, signal });
  }
  signal?.throwIfAborted();
  // An empty bounded year can still point to older candles. Keep startup work
  // sequential and limited to four pages; retain the final continuation cursor.
  let pages = 1;
  while (!result.data?.length && result.exhausted !== true && pages < 4) {
    const nextEnd = result.nextEndDate;
    if (!parseDateOnly(nextEnd) || nextEnd < '1900-01-01' || (endDate && nextEnd >= endDate)) break;
    endDate = nextEnd;
    result = await load({ count: plan.count, endDate, includeCurrent, signal });
    signal?.throwIfAborted();
    pages++;
  }
  return { result, endDate, includeCurrent, liveDenied };
}
